/**
 * Shared geometry for every top-down formation pitch in the app — the
 * match-center lineup pitch (LineupsView), the fantasy squad builder
 * (PitchView), and the Team-of-the-Round screens (team-of-round, totr-predict).
 *
 * Centralising these constants keeps player tokens the same width and rows the
 * same distance apart everywhere, so the three pitches read as ONE consistent
 * component instead of three slightly-different ones. Change a value here and
 * every pitch updates together.
 *
 * All values are layout-only (sizes / spacing). Colours stay in the palette
 * tokens (`bg-pitch`, `border-pitch-line`) so RTL + light/dark are unaffected.
 */

/**
 * Width of a single player token (avatar + name pill + any rating/points chip
 * stacked under it). Every pitch uses this so columns line up across screens and
 * a row of N players always reserves the same horizontal budget.
 */
export const PLAYER_TOKEN_WIDTH = 68;

/**
 * Vertical gap between formation rows. Paired with {@link ROW_MIN_HEIGHT} this
 * guarantees a player's rating / points chip (which hangs *below* the avatar)
 * can never collide with the avatar of the row beneath it.
 */
export const ROW_GAP = 14;

/**
 * Minimum height a formation row reserves. Sized to fit the tallest token stack
 * — avatar (~46) + gap + rating/points chip + name pill — so badges that sit
 * under the avatar always have room and never overlap the next row down.
 */
export const ROW_MIN_HEIGHT = 92;

/** Avatar diameter used for a starting-XI player token across every pitch. */
export const TOKEN_AVATAR_SIZE = 46;

/**
 * Vertical padding inside the turf, above the first row and below the last, so
 * the attacking row and the keeper don't sit flush against the goal-lines.
 */
export const PITCH_VERTICAL_PADDING = 20;
